import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronDown, ChevronRight, Eraser, Hash, ListChecks, Megaphone, MessageCircleReply, MessagesSquare, Pin, Plus, RefreshCw,
  Rss, Send, ShieldCheck, Sparkles, SquareSlash, Trash2, Volume2, Youtube, Twitch, Instagram, Music2, Radio,
} from "lucide-react";
import { botGet, botPost, apiError } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChannelSelect, RoleSelect } from "@/components/pickers";
import {
  ChannelMulti, Empty, FeatureGate, Field, MessageMini, NumberInput, PageHeader, Panel, PillTabs, SaveBar, SimpleSelect, TextInput,
  ToggleRow, cleanMessage, formatDateTime, inputC, outlineBtn, primaryBtn, useFeature, HowItWorks, TabIntro,
} from "@/components/kit";

const del = (onClick) => <Button size="icon" variant="ghost" onClick={onClick} className="text-[#F23F43] hover:bg-[#F23F43]/10"><Trash2 className="h-4 w-4" /></Button>;
const Expand = ({ open, onClick }) => <button onClick={onClick} className="rounded-lg p-1.5 text-[#949BA4] hover:bg-white/5 hover:text-white">{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button>;

// ─────────────────────────── Ruoli e verifica ───────────────────────────
export function RolesVerification() {
  const [tab, setTab] = useState("menus");
  return (
    <div className="space-y-6">
      <PageHeader icon={ListChecks} title="Ruoli e verifica" subtitle="Menu a tendina per scegliere i ruoli da soli e verifica anti-bot per chi entra." />
      <PillTabs active={tab} onChange={setTab} tabs={[{ id: "menus", label: "Menu ruoli", icon: ListChecks }, { id: "verify", label: "Verifica", icon: ShieldCheck }]} />
      {tab === "menus" ? <RoleMenus /> : <Verification />}
    </div>
  );
}

const newPanel = () => ({
  id: `menu-${Date.now().toString(36)}`, name: "Nuovo menu", channelId: null, messageId: null, placeholder: "Scegli i tuoi ruoli", minValues: 0, maxValues: 5, options: [],
  message: { embeds: [{ title: "🎭 Scegli i tuoi ruoli", description: "Usa il menu qui sotto per aggiungere o togliere i ruoli.", color: "#5865F2" }] },
});

function RoleMenus() {
  const f = useFeature("rolemenus");
  const [open, setOpen] = useState(null);
  const publish = async (p) => {
    if (f.dirty) return toast.error("Salva prima le modifiche");
    try { await botPost(`rolemenus/${p.id}/publish`, { channelId: p.channelId || undefined }); toast.success(p.messageId ? "Menu aggiornato su Discord" : "Menu pubblicato"); await f.reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <FeatureGate feature={f}>
      {(v) => {
        const setPanel = (id, patch) => f.change({ panels: v.panels.map((p) => (p.id === id ? { ...p, ...patch } : p)) });
        return (
          <div className="space-y-4">
            {v.panels.length === 0 && <Empty icon={ListChecks} title="Nessun menu ruoli">I membri scelgono i ruoli (colori, notifiche, giochi…) da un menu a tendina.</Empty>}
            {v.panels.map((p) => {
              const expanded = open === p.id;
              const setOpt = (i, patch) => setPanel(p.id, { options: p.options.map((o, j) => (j === i ? { ...o, ...patch } : o)) });
              return (
                <Panel key={p.id}>
                  <div className="flex flex-wrap items-center gap-3">
                    <Expand open={expanded} onClick={() => setOpen(expanded ? null : p.id)} />
                    <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setOpen(expanded ? null : p.id)}>
                      <p className="font-bold text-white">{p.name}</p>
                      <p className="text-xs text-[#949BA4]">{p.options.length} ruoli · {p.messageId ? "pubblicato" : "non ancora pubblicato"}</p>
                    </div>
                    <Button size="sm" onClick={() => publish(p)} disabled={!p.options.length || !p.channelId} className={primaryBtn}><Send className="mr-1.5 h-3.5 w-3.5" />{p.messageId ? "Aggiorna su Discord" : "Pubblica"}</Button>
                    {del(() => f.change({ panels: v.panels.filter((x) => x.id !== p.id) }))}
                  </div>
                  {expanded && (
                    <div className="mt-4 space-y-4 border-t border-[#232838] pt-4">
                      <div className="grid gap-4 md:grid-cols-3">
                        <Field label="Nome (solo per te)"><TextInput value={p.name} onChange={(name) => setPanel(p.id, { name })} /></Field>
                        <Field label="Canale"><ChannelSelect value={p.channelId} onChange={(channelId) => setPanel(p.id, { channelId, messageId: channelId === p.channelId ? p.messageId : null })} /></Field>
                        <Field label="Testo del menu"><TextInput value={p.placeholder} onChange={(placeholder) => setPanel(p.id, { placeholder })} /></Field>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-sm text-[#DBDEE1]">Si possono scegliere da <NumberInput value={p.minValues} min={0} max={25} className="w-20" onChange={(minValues) => setPanel(p.id, { minValues })} /> a <NumberInput value={p.maxValues} min={1} max={25} className="w-20" suffix="ruoli" onChange={(maxValues) => setPanel(p.id, { maxValues })} /></div>
                      <div className="space-y-2">
                        <p className="text-xs font-bold uppercase tracking-wide text-[#B5BAC1]">Ruoli nel menu ({p.options.length}/25)</p>
                        {p.options.map((o, i) => (
                          <div key={i} className="grid gap-2 rounded-xl border border-[#232838] bg-[#131620] p-3 md:grid-cols-[1fr_70px_1fr_1fr_auto]">
                            <RoleSelect value={o.roleId} onChange={(roleId) => setOpt(i, { roleId })} />
                            <TextInput value={o.emoji} onChange={(emoji) => setOpt(i, { emoji })} placeholder="🙂" />
                            <TextInput value={o.label} onChange={(label) => setOpt(i, { label })} placeholder="Etichetta" maxLength={100} />
                            <TextInput value={o.description} onChange={(description) => setOpt(i, { description })} placeholder="Descrizione" maxLength={100} />
                            {del(() => setPanel(p.id, { options: p.options.filter((_, j) => j !== i) }))}
                          </div>
                        ))}
                        <Button size="sm" variant="outline" disabled={p.options.length >= 25} onClick={() => setPanel(p.id, { options: [...p.options, { roleId: null, label: "", emoji: "", description: "" }] })} className={outlineBtn}><Plus className="mr-1 h-4 w-4" />Ruolo</Button>
                      </div>
                      <MessageMini value={p.message} onChange={(message) => setPanel(p.id, { message })} />
                    </div>
                  )}
                </Panel>
              );
            })}
            <Button onClick={() => { const p = newPanel(); f.change({ panels: [...v.panels, p] }); setOpen(p.id); }} className={primaryBtn}><Plus className="mr-2 h-4 w-4" />Nuovo menu ruoli</Button>
            <SaveBar feature={f} prepare={(x) => ({
              panels: x.panels.map((p) => ({
                ...p, message: cleanMessage(p.message), maxValues: Math.max(p.maxValues, 1),
                options: p.options.filter((o) => o.roleId).map((o) => ({ roleId: o.roleId, label: o.label.trim() || "Ruolo", emoji: o.emoji || undefined, description: o.description || undefined })),
              })),
            })} />
          </div>
        );
      }}
    </FeatureGate>
  );
}

/** La verifica ora ha una sezione tutta sua. */
function Verification() {
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-[#5865F2]/30 bg-[#5865F2]/10 p-5">
      <ShieldCheck className="h-8 w-8 text-[#8ea0ff]" />
      <div className="min-w-0 flex-1">
        <p className="font-bold text-white">La verifica ha una sezione tutta sua</p>
        <p className="text-sm text-[#c7cbfb]">Con ingresso libero, protezione dei booster, utenti in attesa e registro.</p>
      </div>
      <Link to="/verifica"><Button className={primaryBtn}>Apri «Verifica ingresso»</Button></Link>
    </div>
  );
}

// ─────────────────────────── Risposte e comandi personalizzati ───────────────────────────
const MATCH = [["contains", "Il messaggio contiene"], ["word", "Contiene la parola esatta"], ["exact", "Il messaggio è esattamente"], ["startsWith", "Inizia con"], ["regex", "Espressione regolare"]].map(([value, label]) => ({ value, label }));

export function Responses() {
  const [tab, setTab] = useState("auto");
  return (
    <div className="space-y-6">
      <PageHeader icon={MessageCircleReply} title="Risposte e comandi" subtitle="Insegna al bot cosa rispondere: in automatico a certe parole, oppure con comandi che crei tu." />
      <HowItWorks id="responses"
        intro="Qui crei risposte tue. È diverso da «Comandi slash», che è l'elenco dei comandi già pronti del bot (/ban, /giveaway…) da accendere o spegnere."
        steps={[
          <><b className="text-white">Risposte automatiche</b>: scegli una parola o frase. Quando qualcuno la scrive in chat, il bot risponde da solo.</>,
          <><b className="text-white">Comandi personalizzati</b>: inventi un comando nuovo (es. <span className="font-mono text-[#8ea0ff]">/social</span>). Chi lo scrive riceve il messaggio che hai preparato.</>,
          <>Premi <b className="text-white">Salva</b>: le risposte sono attive subito, i nuovi comandi compaiono su Discord entro pochi secondi.</>,
        ]}
        example={<>risposta automatica su «ip server» → il bot risponde «L'IP è play.mioserver.it». Comando <span className="font-mono">/regole</span> → mostra le regole a chi lo usa.</>} />
      <PillTabs active={tab} onChange={setTab} tabs={[{ id: "auto", label: "Risposte automatiche", icon: MessageCircleReply }, { id: "custom", label: "Comandi personalizzati", icon: SquareSlash }]} />
      {tab === "auto" ? <AutoResponses /> : <CustomCommands />}
    </div>
  );
}

function AutoResponses() {
  const f = useFeature("autoresponses");
  const [open, setOpen] = useState(null);
  return (
    <FeatureGate feature={f}>
      {(v) => {
        const setItem = (id, patch) => f.change({ items: v.items.map((i) => (i.id === id ? { ...i, ...patch } : i)) });
        return (
          <div className="space-y-4">
            <ToggleRow title={v.enabled ? "Risposte automatiche attive" : "Risposte automatiche disattivate"} desc="Quando un messaggio corrisponde, il bot risponde." checked={v.enabled} onChange={(enabled) => f.change({ enabled })} />
            {v.items.length === 0 && <Empty icon={MessageCircleReply} title="Nessuna risposta automatica">Es. quando qualcuno scrive "ip server" il bot risponde con l'indirizzo.</Empty>}
            {v.items.map((it) => {
              const expanded = open === it.id;
              return (
                <Panel key={it.id}>
                  <div className="flex items-center gap-3">
                    <Expand open={expanded} onClick={() => setOpen(expanded ? null : it.id)} />
                    <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setOpen(expanded ? null : it.id)}>
                      <p className="font-mono font-semibold text-white">“{it.trigger || "…"}”</p>
                      <p className="text-xs text-[#949BA4]">{MATCH.find((m) => m.value === it.match)?.label}{it.cooldownSeconds ? ` · pausa ${it.cooldownSeconds}s` : ""}</p>
                    </div>
                    <Switch checked={it.enabled} onCheckedChange={(enabled) => setItem(it.id, { enabled })} />
                    {del(() => f.change({ items: v.items.filter((i) => i.id !== it.id) }))}
                  </div>
                  {expanded && (
                    <div className="mt-4 space-y-4 border-t border-[#232838] pt-4">
                      <div className="grid gap-4 md:grid-cols-2">
                        <Field label="Quando"><SimpleSelect value={it.match} onChange={(match) => setItem(it.id, { match })} options={MATCH} /></Field>
                        <Field label="Testo attivatore"><TextInput value={it.trigger} onChange={(trigger) => setItem(it.id, { trigger })} maxLength={200} /></Field>
                        <Field label="Solo in questi canali (vuoto = ovunque)"><ChannelMulti value={it.channelIds} onChange={(channelIds) => setItem(it.id, { channelIds })} /></Field>
                        <div className="space-y-2">
                          <ToggleRow title="Rispondi citando il messaggio" checked={it.reply} onChange={(reply) => setItem(it.id, { reply })} />
                          <ToggleRow title="Elimina il messaggio originale" checked={it.deleteTrigger} onChange={(deleteTrigger) => setItem(it.id, { deleteTrigger })} />
                          <div className="flex items-center gap-2 text-sm text-[#DBDEE1]">Pausa <NumberInput value={it.cooldownSeconds} max={86400} className="w-24" suffix="secondi" onChange={(cooldownSeconds) => setItem(it.id, { cooldownSeconds })} /></div>
                        </div>
                      </div>
                      <MessageMini value={it.response} onChange={(response) => setItem(it.id, { response })} />
                    </div>
                  )}
                </Panel>
              );
            })}
            <Button className={primaryBtn} onClick={() => { const id = `ar-${Date.now().toString(36)}`; f.change({ items: [...v.items, { id, enabled: true, trigger: "", match: "contains", response: { content: "" }, reply: true, deleteTrigger: false, channelIds: [], cooldownSeconds: 10 }] }); setOpen(id); }}><Plus className="mr-2 h-4 w-4" />Aggiungi risposta</Button>
            <SaveBar feature={f} prepare={(x) => ({ ...x, items: x.items.filter((i) => i.trigger.trim()).map((i) => ({ ...i, response: cleanMessage(i.response) })) })} />
          </div>
        );
      }}
    </FeatureGate>
  );
}

function CustomCommands() {
  const f = useFeature("customcommands");
  const [open, setOpen] = useState(null);
  return (
    <FeatureGate feature={f}>
      {(v) => {
        const setItem = (i, patch) => f.change({ items: v.items.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
        return (
          <div className="space-y-4">
            {v.items.length === 0 && <Empty icon={SquareSlash} title="Nessun comando personalizzato">Es. /regole, /ip, /social: il bot risponde con il messaggio che scegli.</Empty>}
            {v.items.map((c, i) => (
              <Panel key={i}>
                <div className="flex items-center gap-3">
                  <Expand open={open === i} onClick={() => setOpen(open === i ? null : i)} />
                  <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setOpen(open === i ? null : i)}>
                    <p className="font-mono font-semibold text-[#8ea0ff]">/{c.name || "…"} <span className="font-sans text-sm font-normal text-[#949BA4]">— {c.description}</span></p>
                  </div>
                  {del(() => f.change({ items: v.items.filter((_, j) => j !== i) }))}
                </div>
                {open === i && (
                  <div className="mt-4 space-y-4 border-t border-[#232838] pt-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <Field label="Nome del comando" hint="Minuscole, numeri, - e _"><TextInput value={c.name} onChange={(name) => setItem(i, { name: name.toLowerCase().replace(/[^a-z0-9_-]/g, "") })} maxLength={32} /></Field>
                      <Field label="Descrizione"><TextInput value={c.description} onChange={(description) => setItem(i, { description })} maxLength={100} /></Field>
                      <Field label="Ruolo richiesto (facoltativo)"><RoleSelect value={c.requiredRoleId} onChange={(requiredRoleId) => setItem(i, { requiredRoleId })} placeholder="Tutti" /></Field>
                      <ToggleRow title="Risposta privata" desc="Solo chi usa il comando la vede." checked={c.ephemeral} onChange={(ephemeral) => setItem(i, { ephemeral })} />
                    </div>
                    <MessageMini value={c.response} onChange={(response) => setItem(i, { response })} />
                  </div>
                )}
              </Panel>
            ))}
            <Button className={primaryBtn} onClick={() => { f.change({ items: [...v.items, { name: "", description: "Comando personalizzato", response: { content: "" }, ephemeral: false, requiredRoleId: null }] }); setOpen(v.items.length); }}><Plus className="mr-2 h-4 w-4" />Nuovo comando</Button>
            <SaveBar feature={f} prepare={(x) => ({ items: x.items.filter((c) => c.name).map((c) => ({ ...c, requiredRoleId: c.requiredRoleId || null, response: cleanMessage(c.response) })) })} />
          </div>
        );
      }}
    </FeatureGate>
  );
}

// ─────────────────────────── Canali intelligenti ───────────────────────────
const SMART_HELP = {
  tempvoice: { text: "Crei un solo canale vocale «➕ Crea stanza». Chi ci entra viene spostato in una stanza vocale tutta sua, che sparisce da sola quando si svuota.", example: "Marco entra in «➕ Crea stanza» → nasce «Stanza di Marco» → quando escono tutti viene eliminata." },
  counters: { text: "Canali vocali bloccati il cui nome mostra un numero aggiornato in automatico (membri, online, boost…). Nessuno può entrarci: servono solo da cartellone.", example: "un canale che si chiama sempre «👥 Membri: 152» e cambia quando entra qualcuno." },
  sticky: { text: "Un messaggio che resta sempre l'ultimo del canale: quando qualcuno scrive, il bot lo ripubblica in fondo.", example: "in #vendo-compro resta sempre visibile «Leggi le regole prima di pubblicare»." },
  autothread: { text: "Per ogni messaggio scritto in un canale, il bot apre un thread dove rispondere, così la chat principale resta ordinata.", example: "in #suggerimenti ogni proposta ha la sua discussione separata." },
  autopublish: { text: "Nei canali annunci, i messaggi vengono «pubblicati» in automatico, così arrivano anche ai server che seguono il tuo canale.", example: "scrivi in #annunci e il messaggio viene inoltrato da solo ai server follower." },
  autodelete: { text: "Il bot cancella da solo i messaggi più vecchi di un certo tempo in un canale (i fissati possono restare).", example: "in #spam o #comandi-bot i messaggi spariscono dopo 60 minuti." },
};

export function SmartChannels() {
  const [tab, setTab] = useState("tempvoice");
  return (
    <div className="space-y-6">
      <PageHeader icon={Sparkles} title="Canali intelligenti" subtitle="Canali che si gestiscono da soli: si creano, si aggiornano e si puliscono senza che tu faccia nulla." />
      <HowItWorks id="smartchannels"
        intro="Ogni scheda qui sotto è una piccola automazione per i canali. Accendi solo quelle che ti servono: sono tutte indipendenti."
        steps={[
          <>Scegli una scheda (es. <b className="text-white">Vocali temporanei</b>).</>,
          <>Indica su quali canali deve lavorare il bot.</>,
          <>Premi <b className="text-white">Salva</b>: da quel momento il bot fa tutto da solo.</>,
        ]} />
      <PillTabs active={tab} onChange={setTab} tabs={[
        { id: "tempvoice", label: "Vocali temporanei", icon: Volume2 }, { id: "counters", label: "Contatori", icon: Hash },
        { id: "sticky", label: "Messaggi fissati", icon: Pin }, { id: "autothread", label: "Thread automatici", icon: MessagesSquare },
        { id: "autopublish", label: "Pubblicazione", icon: Megaphone }, { id: "autodelete", label: "Pulizia", icon: Eraser },
      ]} />
      <TabIntro example={SMART_HELP[tab].example}>{SMART_HELP[tab].text}</TabIntro>
      {tab === "tempvoice" && <TempVoice />}
      {tab === "counters" && <Counters />}
      {tab === "sticky" && <Sticky />}
      {tab === "autothread" && <AutoThread />}
      {tab === "autopublish" && <AutoPublish />}
      {tab === "autodelete" && <AutoDelete />}
    </div>
  );
}

function TempVoice() {
  const f = useFeature("tempvoice");
  return (
    <FeatureGate feature={f}>
      {(v) => (
        <div className="space-y-4">
          <ToggleRow title={v.enabled ? "Vocali temporanei attivi" : "Vocali temporanei disattivati"} desc="Chi entra in un canale 'hub' riceve una stanza tutta sua, eliminata quando si svuota." checked={v.enabled} onChange={(enabled) => f.change({ enabled })} />
          <Panel icon={Volume2} title="Impostazioni">
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Canali hub (entra per creare)" hint="Crea un vocale tipo '➕ Crea stanza' e selezionalo."><ChannelMulti value={v.hubChannelIds} types={["voice"]} onChange={(hubChannelIds) => f.change({ hubChannelIds })} /></Field>
              <Field label="Categoria delle stanze" hint="Vuota = la stessa dell'hub."><ChannelSelect value={v.categoryId} types={["category"]} onChange={(categoryId) => f.change({ categoryId })} /></Field>
              <Field label="Nome della stanza" hint="Segnaposto: {user.displayName}"><TextInput value={v.nameTemplate} onChange={(nameTemplate) => f.change({ nameTemplate })} maxLength={100} /></Field>
              <Field label="Limite persone"><NumberInput value={v.userLimit} max={99} suffix="(0 = nessun limite)" onChange={(userLimit) => f.change({ userLimit })} /></Field>
            </div>
            <div className="mt-4"><ToggleRow title="Il proprietario gestisce la stanza" desc="Può rinominarla, mettere un limite, spostare e silenziare." checked={v.ownerCanManage} onChange={(ownerCanManage) => f.change({ ownerCanManage })} /></div>
          </Panel>
          <SaveBar feature={f} />
        </div>
      )}
    </FeatureGate>
  );
}

const COUNTERS = ["👥 Membri: {members}", "🧑 Umani: {humans}", "🤖 Bot: {bots}", "🚀 Boost: {boosts}", "🔊 In vocale: {voice}"];

function Counters() {
  const { refresh } = useData();
  const f = useFeature("counters");
  const [vars, setVars] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ template: COUNTERS[0], categoryId: "" });
  useEffect(() => { botGet("counters/preview").then(setVars).catch(() => {}); }, []);
  const fill = (t) => (vars ? t.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? vars[k] : m)) : t);
  const create = async () => {
    try { await botPost("counters", { template: form.template, categoryId: form.categoryId || null }); toast.success("Contatore creato", { description: fill(form.template) }); setCreating(false); f.reload(); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <FeatureGate feature={f}>
      {(v) => (
        <div className="space-y-4">
          <div className="rounded-2xl border border-[#5865F2]/30 bg-[#5865F2]/10 p-4 text-sm text-[#c7cbfb]">I contatori sono canali vocali bloccati il cui nome mostra un numero, aggiornato ogni 10 minuti (limite di Discord).</div>
          {v.items.length === 0 && <Empty icon={Hash} title="Nessun contatore" />}
          <div className="grid gap-3 md:grid-cols-2">
            {v.items.map((c, i) => (
              <Panel key={c.id}>
                <div className="flex items-center gap-3">
                  <Volume2 className="h-5 w-5 text-[#8ea0ff]" />
                  <div className="min-w-0 flex-1 space-y-1">
                    <TextInput value={c.template} onChange={(template) => f.change({ items: v.items.map((x, j) => (j === i ? { ...x, template } : x)) })} />
                    <p className="text-xs text-[#949BA4]">Anteprima: <span className="font-semibold text-white">{fill(c.template)}</span></p>
                  </div>
                  {del(() => f.change({ items: v.items.filter((_, j) => j !== i) }))}
                </div>
              </Panel>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setCreating(true)} disabled={v.items.length >= 10} className={primaryBtn}><Plus className="mr-2 h-4 w-4" />Nuovo contatore</Button>
            <Button variant="outline" onClick={() => botPost("counters/refresh").then(() => toast.success("Contatori aggiornati")).catch((e) => toast.error(apiError(e)))} className={outlineBtn}><RefreshCw className="mr-2 h-4 w-4" />Aggiorna ora</Button>
          </div>
          <SaveBar feature={f} />
          <Dialog open={creating} onOpenChange={setCreating}>
            <DialogContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
              <DialogHeader><DialogTitle className="text-white">Nuovo contatore</DialogTitle><DialogDescription className="text-[#949BA4]">Anteprima: {fill(form.template)}</DialogDescription></DialogHeader>
              <Field label="Testo"><TextInput value={form.template} onChange={(template) => setForm((x) => ({ ...x, template }))} /></Field>
              <div className="flex flex-wrap gap-1.5">{COUNTERS.map((e) => <button key={e} onClick={() => setForm((x) => ({ ...x, template: e }))} className="rounded-lg border border-[#232838] px-2.5 py-1 text-xs text-[#B5BAC1] hover:border-[#5865F2] hover:text-white">{e}</button>)}</div>
              <Field label="Categoria"><ChannelSelect value={form.categoryId} types={["category"]} onChange={(categoryId) => setForm((x) => ({ ...x, categoryId }))} /></Field>
              <DialogFooter><Button onClick={create} className={primaryBtn}>Crea canale</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </FeatureGate>
  );
}

function ListFeature({ name, info, empty, emptyText, makeItem, prepare, render }) {
  const f = useFeature(name);
  return (
    <FeatureGate feature={f}>
      {(v) => (
        <div className="space-y-4">
          <div className="rounded-2xl border border-[#5865F2]/30 bg-[#5865F2]/10 p-4 text-sm text-[#c7cbfb]">{info}</div>
          {v.items.length === 0 && <Empty icon={empty} title={emptyText} />}
          {v.items.map((item, i) => {
            const set = (patch) => f.change({ items: v.items.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
            return <Panel key={i} right={del(() => f.change({ items: v.items.filter((_, j) => j !== i) }))}>{render(item, set)}</Panel>;
          })}
          <Button onClick={() => f.change({ items: [...v.items, makeItem()] })} className={primaryBtn}><Plus className="mr-2 h-4 w-4" />Aggiungi</Button>
          <SaveBar feature={f} prepare={prepare} />
        </div>
      )}
    </FeatureGate>
  );
}

function Sticky() {
  return <ListFeature name="sticky" empty={Pin} emptyText="Nessun messaggio fissato"
    info="Il messaggio viene ripubblicato come ultimo del canale ogni volta che qualcuno scrive: ideale per regole o istruzioni."
    makeItem={() => ({ channelId: null, message: { embeds: [{ title: "📌 Promemoria", description: "Leggi le regole prima di scrivere!", color: "#5865F2" }] } })}
    prepare={(x) => ({ items: x.items.filter((i) => i.channelId).map((i) => ({ ...i, message: cleanMessage(i.message) })) })}
    render={(item, set) => (
      <div className="space-y-4"><Field label="Canale"><ChannelSelect value={item.channelId} onChange={(channelId) => set({ channelId })} /></Field><MessageMini value={item.message} onChange={(message) => set({ message })} /></div>
    )} />;
}

const ARCHIVE = [[60, "1 ora"], [1440, "1 giorno"], [4320, "3 giorni"], [10080, "1 settimana"]].map(([value, label]) => ({ value, label }));
function AutoThread() {
  return <ListFeature name="autothread" empty={MessagesSquare} emptyText="Nessun canale"
    info="In questi canali ogni nuovo messaggio apre un thread per le risposte (utile per #idee, #aiuto, #media)."
    makeItem={() => ({ channelId: null, nameTemplate: "Discussione di {user.displayName}", archiveMinutes: 1440 })}
    prepare={(x) => ({ items: x.items.filter((i) => i.channelId) })}
    render={(item, set) => (
      <div className="grid gap-4 md:grid-cols-3">
        <Field label="Canale"><ChannelSelect value={item.channelId} onChange={(channelId) => set({ channelId })} /></Field>
        <Field label="Nome del thread"><TextInput value={item.nameTemplate} onChange={(nameTemplate) => set({ nameTemplate })} /></Field>
        <Field label="Archivia dopo"><SimpleSelect value={item.archiveMinutes} onChange={(v) => set({ archiveMinutes: Number(v) })} options={ARCHIVE} /></Field>
      </div>
    )} />;
}

const AGES = [[5, "5 minuti"], [30, "30 minuti"], [60, "1 ora"], [360, "6 ore"], [1440, "1 giorno"], [10080, "1 settimana"], [43200, "30 giorni"]].map(([value, label]) => ({ value, label }));
function AutoDelete() {
  return <ListFeature name="autodelete" empty={Eraser} emptyText="Nessun canale"
    info="⚠️ I messaggi più vecchi del tempo scelto vengono eliminati per sempre. Usalo per canali temporanei (es. #bot-comandi)."
    makeItem={() => ({ channelId: null, afterMinutes: 1440, keepPinned: true })}
    prepare={(x) => ({ items: x.items.filter((i) => i.channelId) })}
    render={(item, set) => (
      <div className="grid gap-4 md:grid-cols-3">
        <Field label="Canale"><ChannelSelect value={item.channelId} onChange={(channelId) => set({ channelId })} /></Field>
        <Field label="Elimina i messaggi più vecchi di"><SimpleSelect value={item.afterMinutes} onChange={(v) => set({ afterMinutes: Number(v) })} options={AGES} /></Field>
        <ToggleRow title="Tieni i fissati" checked={item.keepPinned} onChange={(keepPinned) => set({ keepPinned })} />
      </div>
    )} />;
}

function AutoPublish() {
  const f = useFeature("autopublish");
  return (
    <FeatureGate feature={f}>
      {(v) => (
        <div className="space-y-4">
          <Panel icon={Megaphone} title="Pubblicazione automatica" desc="Nei canali annunci i messaggi (anche del bot e dei webhook) vengono pubblicati da soli nei server che seguono il canale.">
            <Field label="Canali annunci"><ChannelMulti value={v.channelIds} types={["announcement"]} onChange={(channelIds) => f.change({ channelIds })} /></Field>
          </Panel>
          <SaveBar feature={f} />
        </div>
      )}
    </FeatureGate>
  );
}

// ─────────────────────────── Notifiche social ───────────────────────────
const LIVE_DEF = { preview: true, refreshMinutes: 15, watchButton: true, onEnd: "edit" };
/** Opzioni della scheda della live: anteprima che si aggiorna, pulsante, cosa fare a fine live. */
function LiveOptions({ value, onChange, color }) {
  const v = { ...LIVE_DEF, ...(value || {}) };
  const set = (p) => onChange({ ...v, ...p });
  return (
    <div className="space-y-3 rounded-xl border p-4" style={{ borderColor: `${color}55`, background: `${color}0d` }}>
      <p className="text-sm font-bold text-white">📺 Scheda della live</p>
      <div className="grid gap-3 md:grid-cols-2">
        <ToggleRow title="Mostra l'anteprima della live" desc="Un fotogramma della live preso da Twitch, grande nella notifica." checked={v.preview} onChange={(preview) => set({ preview })} />
        <ToggleRow title="Pulsante «🔴 Guarda la live»" desc="Un tasto che porta dritto alla live." checked={v.watchButton} onChange={(watchButton) => set({ watchButton })} />
        <Field label="Aggiorna l'anteprima ogni" hint="Cambia il fotogramma, il titolo, il gioco e il numero di spettatori mentre la live va avanti.">
          <SimpleSelect value={v.refreshMinutes} onChange={(x) => set({ refreshMinutes: Number(x) })} options={[{ value: 5, label: "5 minuti" }, { value: 10, label: "10 minuti" }, { value: 15, label: "15 minuti" }, { value: 30, label: "30 minuti" }, { value: 60, label: "1 ora" }, { value: 0, label: "Mai (resta quella dell'inizio)" }]} />
        </Field>
        <Field label="Quando la live finisce">
          <SimpleSelect value={v.onEnd} onChange={(onEnd) => set({ onEnd })} options={[{ value: "edit", label: "Scrivi «Live terminata» con la durata" }, { value: "keep", label: "Lascia il messaggio com'è" }, { value: "delete", label: "Elimina il messaggio" }]} />
        </Field>
      </div>
      <p className="text-[11px] text-[#949BA4]">Se il messaggio non ha un embed, il bot crea da solo una scheda con foto dello streamer, titolo, gioco, spettatori e anteprima. Il video vero della live non si può mettere dentro un messaggio di un bot (limite di Discord): l'anteprima si aggiorna da sola e il titolo e il pulsante aprono la live su Twitch.</p>
    </div>
  );
}

const KickGlyph = ({ className }) => <span className={`${className} flex items-center justify-center text-[15px] font-black leading-none`}>K</span>;
const FEED_TYPES = {
  youtube: { label: "YouTube", what: "Nuovi video", color: "#FF0033", icon: Youtube, source: "Link del canale, @nome o ID (UC…)", example: "https://www.youtube.com/@nomecanale", message: "📺 **{author}** ha pubblicato un nuovo video!\n**{title}**\n{url}" },
  twitch: { label: "Twitch", what: "Inizio live", color: "#9146FF", icon: Twitch, live: true, source: "Nome utente Twitch", example: "nomestreamer", message: "🔴 **{author}** è in live su Twitch! Corri a vederlo 👇" },
  kick: { label: "Kick", what: "Inizio live", color: "#53FC18", icon: KickGlyph, live: true, source: "Nome del canale Kick", example: "nomestreamer", message: "🟢 **{author}** è in live su Kick! Corri a vederlo 👇" },
  instagram: { label: "Instagram", what: "Nuovi post", color: "#E1306C", icon: Instagram, rsshub: true, source: "Nome utente Instagram", example: "@nomeutente", message: "📸 Nuovo post di **{author}** su Instagram!\n{url}" },
  tiktok: { label: "TikTok", what: "Nuovi video", color: "#25F4EE", icon: Music2, rsshub: true, source: "Nome utente TikTok", example: "@nomeutente", message: "🎵 **{author}** ha pubblicato un nuovo TikTok!\n{title}\n{url}" },
  rss: { label: "Sito / blog (RSS)", what: "Nuovi articoli", color: "#F99B1D", icon: Rss, source: "Indirizzo del feed RSS o Atom", example: "https://sito.it/feed", message: "📰 **{title}**\n{url}" },
};

const BrandIcon = ({ type, size = "h-11 w-11" }) => {
  const T = FEED_TYPES[type] || FEED_TYPES.rss;
  return (
    <div className={`flex ${size} shrink-0 items-center justify-center rounded-xl`} style={{ background: `${T.color}22`, color: T.color, boxShadow: `0 0 18px -8px ${T.color}` }}>
      <T.icon className="h-5 w-5" />
    </div>
  );
};

export function Feeds() {
  const f = useFeature("feeds");
  const [open, setOpen] = useState(null);
  const [status, setStatus] = useState({});
  useEffect(() => { botGet("feeds/status").then(setStatus).catch(() => {}); }, []);
  const test = async (id) => {
    if (f.dirty) return toast.error("Salva prima le modifiche");
    try { const r = await botPost(`feeds/${id}/test`); toast.success("Notifica di prova inviata", { description: r.title }); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-6">
      <PageHeader icon={Rss} title="Notifiche social" subtitle="Il bot annuncia da solo nel tuo server nuovi video, post e live dei profili che scegli (controllo ogni 5 minuti)." />
      <FeatureGate feature={f}>
        {(v) => {
          const setItem = (id, patch) => f.change({ items: v.items.map((i) => (i.id === id ? { ...i, ...patch } : i)) });
          const add = (type) => { const id = `feed-${Date.now().toString(36)}`; f.change({ items: [...v.items, { id, enabled: true, type, source: "", channelId: null, roleId: null, message: { content: FEED_TYPES[type].message } }] }); setOpen(id); };
          const has = (pred) => v.items.some((i) => pred(FEED_TYPES[i.type] || {}, i.type));
          return (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                {Object.entries(FEED_TYPES).map(([type, T]) => (
                  <button key={type} onClick={() => add(type)} data-testid={`feed-add-${type}`}
                    className="group flex flex-col items-start gap-3 rounded-2xl border border-[#232838] bg-[#161923] p-4 text-left transition-all hover:-translate-y-0.5"
                    style={{ "--c": T.color }} onMouseEnter={(e) => (e.currentTarget.style.borderColor = `${T.color}80`)} onMouseLeave={(e) => (e.currentTarget.style.borderColor = "")}>
                    <BrandIcon type={type} />
                    <div><p className="font-bold text-white">{T.label}</p><p className="text-xs text-[#949BA4]">{T.what}</p></div>
                  </button>
                ))}
              </div>
              {v.items.length === 0 && <Empty icon={Rss} title="Nessuna notifica">Scegli una piattaforma qui sopra, incolla il nome del profilo e il canale dove annunciare.</Empty>}
              {v.items.map((it) => {
                const T = FEED_TYPES[it.type] || FEED_TYPES.rss;
                const st = status[it.id];
                const expanded = open === it.id;
                return (
                  <Panel key={it.id}>
                    <div className="flex flex-wrap items-center gap-3">
                      <Expand open={expanded} onClick={() => setOpen(expanded ? null : it.id)} />
                      <BrandIcon type={it.type} size="h-9 w-9" />
                      <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setOpen(expanded ? null : it.id)}>
                        <p className="truncate font-bold text-white">{it.source || `Nuovo ${T.label}`}</p>
                        <p className="text-xs text-[#949BA4]">{T.label} · {T.what.toLowerCase()}{st?.lastCheck && ` · controllato ${formatDateTime(st.lastCheck)}`}{st?.error && <span className="text-[#ff7a7d]"> · {st.error}</span>}</p>
                      </div>
                      <Button size="sm" variant="outline" disabled={!it.source || !it.channelId} onClick={() => test(it.id)} className={outlineBtn}><Send className="mr-1.5 h-3.5 w-3.5" />Prova</Button>
                      <Switch checked={it.enabled} onCheckedChange={(enabled) => setItem(it.id, { enabled })} />
                      {del(() => f.change({ items: v.items.filter((i) => i.id !== it.id) }))}
                    </div>
                    {expanded && (
                      <div className="mt-4 space-y-4 border-t border-[#232838] pt-4">
                        <div className="grid gap-4 md:grid-cols-3">
                          <Field label={T.source}><TextInput value={it.source} onChange={(source) => setItem(it.id, { source })} placeholder={T.example} /></Field>
                          <Field label="Canale dove annunciare"><ChannelSelect value={it.channelId} onChange={(channelId) => setItem(it.id, { channelId })} /></Field>
                          <Field label="Ruolo da menzionare"><RoleSelect value={it.roleId} onChange={(roleId) => setItem(it.id, { roleId })} placeholder="Nessuno" /></Field>
                        </div>
                        {T.live && <LiveOptions value={it.live} onChange={(live) => setItem(it.id, { live })} color={T.color} />}
                        <MessageMini value={it.message} onChange={(message) => setItem(it.id, { message })} hint={`Segnaposto: {title} {url} {author}${T.live ? " {game} {viewers} {thumbnail} {avatar}" : ""}`} />
                      </div>
                    )}
                  </Panel>
                );
              })}
              {has((_, t) => t === "twitch") && (
                <Panel title="Credenziali Twitch" icon={Twitch} desc="Sono la «chiave» con cui il bot può chiedere a Twitch se lo streamer è in live. Twitch le dà gratis a chiunque: si fanno una volta sola, in 2 minuti.">
                  <ol className="mb-4 space-y-1.5 rounded-xl border border-[#9146FF]/30 bg-[#9146FF]/[0.07] p-4 text-sm text-[#DBDEE1]">
                    <li><b className="text-white">1.</b> Apri <a href="https://dev.twitch.tv/console/apps/create" target="_blank" rel="noreferrer" className="font-semibold text-[#bf94ff] underline">dev.twitch.tv/console/apps/create</a> ed entra con il tuo account Twitch (se chiede di attivare la verifica in due passaggi, attivala).</li>
                    <li><b className="text-white">2.</b> <b>Nome</b>: quello che vuoi (es. <i>KALYX Bot</i>). <b>URL di reindirizzamento OAuth</b>: scrivi <code className="rounded bg-black/30 px-1">{window.location.origin.startsWith("https") ? window.location.origin : "https://kalyxbot.it"}</code> e premi <b>Aggiungi</b> (il bot non lo usa, ma Twitch lo vuole con https).</li>
                    <li><b className="text-white">3.</b> <b>Categoria</b>: «Chat Bot». <b>Tipo di client</b>: «Riservato». Premi <b>Crea</b>.</li>
                    <li><b className="text-white">4.</b> Clicca <b>Gestisci</b> sull'app appena creata: copia il <b>Client ID</b> e incollalo qui sotto; poi premi <b>Nuovo segreto</b>, copia il <b>Client Secret</b> e incollalo qui sotto.</li>
                    <li><b className="text-white">5.</b> Premi <b>Salva</b> e poi <b>Prova</b> sulla notifica (funziona se lo streamer è in live in quel momento).</li>
                  </ol>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Client ID"><TextInput value={v.twitchClientId} onChange={(twitchClientId) => f.change({ twitchClientId })} /></Field>
                    <Field label="Client Secret"><input type="password" autoComplete="off" value={v.twitchClientSecret} onChange={(e) => f.change({ twitchClientSecret: e.target.value })} className={`flex h-9 w-full rounded-md border px-3 text-sm ${inputC}`} /></Field>
                  </div>
                </Panel>
              )}
              {has((_, t) => t === "kick") && (
                <Panel title="Credenziali Kick" icon={Radio} desc="Su kick.com vai in Impostazioni → Sviluppatore, crea un'app (URL di reindirizzamento: https://kalyxbot.it) e incolla qui Client ID e Client Secret.">
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Client ID"><TextInput value={v.kickClientId || ""} onChange={(kickClientId) => f.change({ kickClientId })} /></Field>
                    <Field label="Client Secret"><input type="password" autoComplete="off" value={v.kickClientSecret || ""} onChange={(e) => f.change({ kickClientSecret: e.target.value })} className={`flex h-9 w-full rounded-md border px-3 text-sm ${inputC}`} /></Field>
                  </div>
                </Panel>
              )}
              {has((T) => T.rsshub) && (
                <Panel title="Instagram e TikTok" icon={Instagram} desc="Instagram e TikTok non offrono un modo gratuito per leggere i post di un profilo: il bot usa RSSHub, un servizio che li trasforma in feed. Quello pubblico è spesso limitato; per notifiche affidabili installalo sulla tua VPS (docs.rsshub.app) e scrivi qui il suo indirizzo.">
                  <Field label="Indirizzo RSSHub"><TextInput value={v.rsshubUrl || ""} onChange={(rsshubUrl) => f.change({ rsshubUrl })} placeholder="https://rsshub.app" /></Field>
                </Panel>
              )}
              <SaveBar feature={f} prepare={(x) => ({ ...x, rsshubUrl: (x.rsshubUrl || "").trim(), items: x.items.filter((i) => i.source.trim() && i.channelId).map((i) => ({ ...i, roleId: i.roleId || null, source: i.source.trim(), message: cleanMessage(i.message) })) })} />
            </div>
          );
        }}
      </FeatureGate>
    </div>
  );
}
